import type { TodoFilter } from "@engram/core";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { applyPalette, type PaletteId } from "./palettes";

export type SortKey = "updated" | "created" | "title" | "deleted"; // "deleted": Trash only
export type ViewMode = "snippets" | "cards" | "side" | "top";
export type Theme = "system" | "light" | "dark";

interface Prefs {
  sort: SortKey;
  sortDesc: boolean;
  trashSort: SortKey; // the Trash keeps its own order, newest deleted first
  trashSortDesc: boolean;
  view: ViewMode;
  showImages: boolean;
  showBody: boolean;
  showTags: boolean;
  showGroups: boolean;
  showCompletedTasks: boolean;
  listCollapsed: boolean;
  sidebarCollapsed: boolean;
  /** Column sizes the owner dragged (null = default): sidebar and list widths, Top-list height. */
  sidebarWidth: number | null;
  listWidth: number | null;
  listHeight: number | null;
  theme: Theme;
  /** Colour theme (account menu > Color theme); "engram" is the stylesheet's own. */
  palette: PaletteId;
  /** Evernote's "Collapsible sections" (note ··· menu): fold chevrons on headings. */
  collapsibleSections: boolean;
  /** Home's Notebooks widget: most recently edited first, or most used (opened and edited lately). */
  homeNotebooks: "recent" | "used";
  /** Home's To-do panel: All, Today (due by tonight, overdue too) or Flagged, like the Android widget's chip. */
  homeTodo: TodoFilter;
  /** Android app: show "Restart" for a new version instead of using it at the next start by itself. */
  askBeforeUpdating: boolean;
  /** Android app: the app update (version code) whose banner was closed. */
  dismissedApk: number;
  expanded: Record<string, boolean>; // sidebar sections / stacks
  set: (p: Partial<Omit<Prefs, "set" | "toggle">>) => void;
  toggle: (key: string) => void;
}

/** Per-device UI preferences (Evernote keeps these per device too). */
export const usePrefs = create<Prefs>()(
  persist(
    (set) => ({
      sort: "updated",
      sortDesc: true,
      trashSort: "deleted",
      trashSortDesc: true,
      view: "snippets",
      showImages: true,
      showBody: true,
      showTags: false,
      showGroups: false,
      showCompletedTasks: true, // Evernote lists them, greyed out, under the open ones
      listCollapsed: false,
      sidebarCollapsed: false,
      sidebarWidth: null,
      listWidth: null,
      listHeight: null,
      theme: "system",
      palette: "engram",
      collapsibleSections: false,
      homeNotebooks: "recent",
      homeTodo: "all",
      askBeforeUpdating: false,
      dismissedApk: 0,
      expanded: { notebooks: false, shortcuts: false },
      set: (p) => set(p),
      toggle: (key) => set((s) => ({ expanded: { ...s.expanded, [key]: !s.expanded[key] } })),
    }),
    { name: "engram.prefs" },
  ),
);

export function applyTheme(theme: Theme, palette: PaletteId = "engram") {
  const root = document.documentElement;
  if (theme === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", theme);
  const dark = theme === "dark" || (theme === "system" && matchMedia("(prefers-color-scheme: dark)").matches);
  applyPalette(palette, dark, theme);
}
